import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthPageLayout } from '@components/auth/AuthPageLayout';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { PasswordStrengthIndicator } from '@components/auth/PasswordStrengthIndicator';
import { validatePassword } from '@/utils/passwordSecurity';
import { authService } from '@/services/auth';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validate = () => {
    const next: typeof errors = {};
    const passwordValidation = validatePassword(password);
    if (!password) {
      next.password = 'Password is required';
    } else if (!passwordValidation.isValid) {
      next.password = passwordValidation.error || 'Password does not meet security requirements';
    }
    if (!confirmPassword) {
      next.confirmPassword = 'Confirm your password';
    } else if (password !== confirmPassword) {
      next.confirmPassword = 'Passwords do not match';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Reset link is invalid or expired');
      return;
    }
    if (!validate()) return;

    setIsLoading(true);
    try {
      await authService.confirmPasswordReset(token, password);
      toast.success('Password updated — you can sign in now');
      navigate('/login');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPageLayout
        title="Invalid reset link"
        subtitle="This password reset link is missing or has expired. Request a new one below."
        alternatePrompt="Need a new link?"
        alternateLinkTo="/forgot-password"
        alternateLinkLabel="Request reset"
        legalAction="resetting your password"
      >
        <div className="text-center">
          <Link
            to="/forgot-password"
            className="inline-flex items-center justify-center rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400"
          >
            Request new reset link
          </Link>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Choose a new password"
      subtitle="Enter a new password for your Khiimori account."
      alternatePrompt="Remember your password?"
      alternateLinkTo="/login"
      alternateLinkLabel="Sign in"
      legalAction="resetting your password"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label="New password"
            type="password"
            placeholder="Min 8 chars with upper, lower, number, special"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            autoComplete="new-password"
            aria-required="true"
            passwordToggle
          />
          <PasswordStrengthIndicator password={password} />
        </div>
        <Input
          label="Confirm new password"
          type="password"
          placeholder="Re-enter your password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirmPassword}
          autoComplete="new-password"
          aria-required="true"
          passwordToggle
        />
        <Button type="submit" className="w-full" isLoading={isLoading}>
          Update password
        </Button>
      </form>
    </AuthPageLayout>
  );
}
